import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/utils/dateFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Calendar, ChevronRight, Clock, CheckCircle, Mail, Loader2, Trophy, Users, Printer, Info } from "lucide-react";

const API = '/api';

export default function Events() {
  const navigate = useNavigate();
  const { isAdmin, getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    start_date: "",
    end_date: "",
    description: "",
    registration_fee: "",
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?t=${Date.now()}`);
      // Fetch matches count for each event
      const eventsWithMatches = await Promise.all(
        res.data.map(async (event) => {
          const matchesRes = await axios.get(`${API}/matches?event_id=${event.id}&t=${Date.now()}`);
          return { ...event, matches: matchesRes.data };
        })
      );
      setEvents(eventsWithMatches);
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Event created");
      }

      setDialogOpen(false);
      resetForm();
      setTimeout(() => fetchEvents(), 100);
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleStatusChange = async (event, newStatus) => {
    try {
      await axios.patch(`${API}/events/${event.id}/status`, { status: newStatus }, {
        headers: getAuthHeader()
      });
      toast.success(`Event status updated to ${newStatus}`);
      setTimeout(() => fetchEvents(), 100);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event and all its matches?")) return;
    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("Event deleted");
      setTimeout(() => fetchEvents(), 100);
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      start_date: event.start_date,
      end_date: event.end_date,
      description: event.description || "",
      registration_fee: event.registration_fee || "",
    });
    setDialogOpen(true);
  };

  const openEmailDialog = async (event) => {
    try {
      const res = await axios.get(`${API}/events/${event.id}/shooter-emails`, {
        headers: getAuthHeader()
      });
      setEmailPreview({
        event,
        ...res.data
      });
      setEmailDialogOpen(true);
    } catch (error) {
      toast.error("Failed to get shooter emails");
    }
  };

  const sendResultsEmail = async () => {
    if (!emailPreview) return;
    
    setSendingEmails(true);
    try {
      const res = await axios.post(
        `${API}/events/${emailPreview.event.id}/email-results`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setEmailDialogOpen(false);
      setEmailPreview(null);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send emails";
      toast.error(message);
    } finally {
      setSendingEmails(false);
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      name: "",
      start_date: "",
      end_date: "",
      description: "",
      registration_fee: "",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800"><Clock size={12} className="mr-1" /> Active</Badge>;
      case "completed":
        return <Badge className="bg-blue-100 text-blue-800"><CheckCircle size={12} className="mr-1" /> Completed</Badge>;
      case "closed":
        return <Badge className="bg-slate-100 text-slate-600"><CheckCircle size={12} className="mr-1" /> Closed</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="events-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Events
          </h1>
          <p className="text-slate-500 mt-1">Manage shooting events and their matches</p>
        </div>

        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="create-event-btn"
              >
                <Plus size={20} className="mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  {editingEvent ? "Edit Event" : "Create Event"}
                </DialogTitle>
                <DialogDescription>
                  Events are containers for matches. Example: "2026 Spring Shoot"
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Event Name</Label>
                  <Input
                    id="name"
                    data-testid="event-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., 2026 Spring Shoot"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Event details..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="registration_fee">Registration Fee ($)</Label>
                  <Input
                    id="registration_fee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.registration_fee}
                    onChange={(e) => setFormData({ ...formData, registration_fee: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <Button type="submit" className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                  {editingEvent ? "Update Event" : "Create Event"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No events yet. Create your first event!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event.id} className="card-hover" data-testid={`event-card-${event.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="font-serif text-xl font-semibold text-slate-800">
                        {event.name}
                      </h3>
                      {getStatusBadge(event.status)}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
                      <span className="font-mono">{formatDate(event.start_date)} - {formatDate(event.end_date)}</span>
                      <span>{event.matches?.length || 0} matches</span>
                    </div>

                    {event.description && (
                      <p className="text-sm text-slate-600 mb-3">{event.description}</p>
                    )}

                    {/* Matches list */}
                    {event.matches?.length > 0 && (
                      <div className="border-t pt-3 mt-3">
                        <p className="text-xs text-slate-500 uppercase mb-2">Matches</p>
                        <div className="flex flex-wrap gap-2">
                          {event.matches.map((match) => (
                            <Button
                              key={match.id}
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/matches/${event.id}`)}
                              className="text-xs"
                            >
                              {match.name}
                              {isAdmin() && (
                                <span className="ml-1 text-slate-400">${match.entry_fee}</span>
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => navigate(`/event-info/${event.id}`)}
                      variant="outline"
                      className="text-slate-700 hover:bg-slate-50"
                      data-testid={`event-info-btn-${event.id}`}
                    >
                      <Info size={16} className="mr-1" />
                      Event Info
                    </Button>
                    <Button
                      onClick={() => navigate(`/matches/${event.id}`)}
                      className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                    >
                      {isAdmin() ? "Manage Matches" : "View Matches"} <ChevronRight size={16} className="ml-1" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/event-leaderboards/${event.id}`)}
                      className="text-amber-600 hover:bg-amber-50"
                    >
                      <Trophy size={16} className="mr-1" />
                      View Leaderboard
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => window.open(`/print-schedule/${event.id}`, "_blank", "noopener,noreferrer")}
                      className="text-slate-700 hover:bg-slate-50"
                      data-testid={`print-schedule-btn-${event.id}`}
                    >
                      <Printer size={16} className="mr-1" />
                      Print Schedule
                    </Button>
                    
                    {isAdmin() && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/event-registrations/${event.id}`)}
                          className="text-green-600 hover:bg-green-50"
                          data-testid={`event-registrations-btn-${event.id}`}
                        >
                          <Users size={16} className="mr-1" />
                          Registrations
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEmailDialog(event)}
                          className="text-blue-600 hover:bg-blue-50"
                          data-testid={`email-results-btn-${event.id}`}
                        >
                          <Mail size={14} className="mr-1" />
                          Email Results
                        </Button>
                        <div className="flex gap-2">
                          {event.status === "active" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(event, "completed")}
                            >
                              Complete Event
                            </Button>
                          )}
                          {event.status === "completed" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStatusChange(event, "closed")}
                              >
                                Close Event
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStatusChange(event, "active")}
                              >
                                Reactivate
                              </Button>
                            </>
                          )}
                          {event.status === "closed" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(event, "active")}
                            >
                              Reactivate
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(event)}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(event.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Email Results Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Mail className="text-blue-600" />
              Email Event Results
            </DialogTitle>
            <DialogDescription>
              Send final results to all shooters who participated
            </DialogDescription>
          </DialogHeader>
          
          {emailPreview && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg">{emailPreview.event_name}</h3>
                <div className="mt-2 space-y-1 text-sm">
                  <p>Total Shooters: <span className="font-medium">{emailPreview.total_shooters}</span></p>
                  <p>Shooters with Email: <span className="font-medium text-green-600">{emailPreview.shooters_with_email}</span></p>
                </div>
              </div>

              {emailPreview.emails.length > 0 ? (
                <>
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Recipients ({emailPreview.emails.length}):</p>
                    <div className="max-h-40 overflow-y-auto bg-white border rounded p-2 space-y-1">
                      {emailPreview.emails.map((e, i) => (
                        <div key={i} className="text-sm flex justify-between">
                          <span>{e.name}</span>
                          <span className="text-slate-500">{e.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={sendResultsEmail}
                    disabled={sendingEmails}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-send-emails-btn"
                  >
                    {sendingEmails ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Send to {emailPreview.emails.length} Shooters
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-4 text-amber-600">
                  <p>No shooter email addresses found.</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Make sure shooters have email addresses in their profiles.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
